<template>
  <div class="music-generator-container">
    <h1 class="title">AI 智能生歌系统</h1>

    <el-row :gutter="20">
      <!-- 左侧：生成配置区 -->
      <el-col :xs="24" :sm="24" :md="12">
        <el-card shadow="hover">
          <div slot="header">生成配置</div>

          <el-form label-width="80px">

            <!-- 场景 -->
            <el-form-item label="场景">
              <el-select
                v-model="form.scene"
                placeholder="请选择场景"
                style="width: 100%"
              >
                <el-option label="日常放松" value="日常放松" />
                <el-option label="工作专注" value="工作专注" />
                <el-option label="浪漫约会" value="浪漫约会" />
                <el-option label="运动健身" value="运动健身" />
                <el-option label="生日祝福" value="生日祝福" />
                <el-option label="毕业纪念" value="毕业纪念" />
                <el-option label="旅行" value="旅行" />
                <el-option label="告白" value="告白" />
              </el-select>
            </el-form-item>

            <!-- 曲风 -->
            <el-form-item label="曲风">
              <el-select
                v-model="form.style"
                placeholder="请选择曲风"
                style="width: 100%"
              >
                <el-option label="流行" value="流行" />
                <el-option label="古风" value="古风" />
                <el-option label="摇滚" value="摇滚" />
                <el-option label="电子" value="电子" />
                <el-option label="民谣" value="民谣" />
                <el-option label="说唱" value="说唱" />
                <el-option label="轻音乐" value="轻音乐" />
              </el-select>
            </el-form-item>

            <!-- 想法 -->
            <el-form-item label="想法">
              <div class="input-with-button">
                <el-input
                  v-model="form.idea"
                  type="textarea"
                  :rows="3"
                  maxlength="500"
                  show-word-limit
                  placeholder="描述你的歌曲想法，也可以点击右侧 AI 生成"
                  class="flex-input"
                />

                <el-button
                  type="warning"
                  plain
                  size="small"
                  :loading="ideaLoading"
                  @click="generateIdea"
                  class="side-button"
                >
                  ✨ 生成想法
                </el-button>
              </div>
            </el-form-item>

            <!-- 歌词 -->
            <el-form-item label="歌词">
              <el-input
                v-model="form.lyrics"
                type="textarea"
                :rows="10"
                maxlength="5000"
                show-word-limit
                placeholder="输入歌词，或者通过 AI 自动生成歌词"
              />

              <el-button
                type="primary"
                plain
                size="small"
                :loading="lyricLoading"
                @click="generateLyrics"
                style="margin-top: 10px"
              >
                ✨ 一键生成歌词
              </el-button>
            </el-form-item>

            <!-- 生歌按钮 -->
            <el-form-item>
              <el-button
                type="success"
                size="large"
                :loading="songLoading"
                :disabled="songLoading"
                style="width: 100%"
                @click="generateSong"
              >
                {{ songLoading ? '歌曲生成中...' : '🎵 生成歌曲' }}
              </el-button>

              <div
                v-if="songLoading && songStatusText"
                class="generation-status"
              >
                {{ songStatusText }}
              </div>
            </el-form-item>

          </el-form>
        </el-card>
      </el-col>

      <!-- 右侧：历史记录 -->
      <el-col :xs="24" :sm="24" :md="12">
        <el-card shadow="hover">
          <div slot="header" class="history-header">
            <span>生成历史</span>

            <el-button
              v-if="history.length"
              type="text"
              size="mini"
              @click="clearHistory"
            >
              清空
            </el-button>
          </div>

          <div
            v-if="history.length === 0"
            class="empty-history"
          >
            暂无生成记录
          </div>

          <div
            v-for="item in history"
            :key="item.id"
            class="history-card"
          >
            <div class="history-main">

              <!-- 封面 -->
              <img
                v-if="item.imageUrl"
                :src="item.imageUrl"
                class="song-cover"
                alt="歌曲封面"
              />

              <div class="history-info">

                <div class="song-title">
                  {{ item.title || 'AI 生成歌曲' }}
                </div>

                <div class="song-meta">
                  <span class="tag">
                    {{ item.scene }}
                  </span>

                  <span class="tag">
                    {{ item.style }}
                  </span>

                  <span
                    v-if="item.duration"
                    class="duration"
                  >
                    {{ formatDuration(item.duration) }}
                  </span>
                </div>

                <p class="lyrics-preview">
                  {{ getLyricsPreview(item) }}
                </p>

              </div>
            </div>

            <div class="history-actions">

              <el-button
                :type="item.isPlaying ? 'danger' : 'primary'"
                size="mini"
                :disabled="!item.url"
                @click="togglePlay(item)"
              >
                {{ item.isPlaying ? '暂停' : '播放' }}
              </el-button>

              <el-button
                size="mini"
                :disabled="!item.url"
                @click="downloadSong(item)"
              >
                下载
              </el-button>

            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 全局音频播放器 -->
    <audio
      ref="globalPlayer"
      preload="none"
      @ended="onAudioEnded"
      @error="onAudioError"
    />
  </div>
</template>

<script>
import axios from 'axios';

/**
 * =========================================
 * API
 * =========================================
 *
 * 注意：
 *
 * 浏览器不直接访问 DeepSeek / AIMusic，
 * 而是通过 Vue 开发代理或你的正式后端代理。
 *
 * 这样 Secret Key 不会出现在前端 JS 中。
 */

const DEEPSEEK_API = '/api/deepseek/chat/completions';

const AIMUSIC_CREATE_API =
  '/api/aimusic/v1/sonic/create';

const AIMUSIC_TASK_API =
  '/api/aimusic/v1/sonic/task/';

/**
 * DeepSeek
 *
 * 当前用于生成想法/歌词，
 * Flash 已经足够，而且速度更快。
 */
const DEEPSEEK_MODEL = 'deepseek-v4-flash';

/**
 * AIMusic Sonic 模型
 *
 * 先使用相对稳定的 sonic-v4-5。
 *
 * 后面确认账号套餐和费用以后可以切换：
 *
 * sonic-v5
 * sonic-v5-5
 */
const AIMUSIC_MODEL = 'sonic-v4-5';

/**
 * AIMusic 官方建议：
 *
 * 15 ~ 25 秒轮询一次。
 */
const AIMUSIC_POLL_INTERVAL = 15000;

/**
 * 最多轮询 40 次。
 *
 * 15 秒 * 40 = 10 分钟。
 */
const AIMUSIC_MAX_POLL_COUNT = 40;

const HISTORY_STORAGE_KEY =
  'ai_music_generator_history_v2';


export default {

  name: 'MusicGenerator',

  data() {
    return {

      form: {
        scene: '',
        style: '',
        idea: '',
        lyrics: ''
      },

      ideaLoading: false,

      lyricLoading: false,

      songLoading: false,

      songStatusText: '',

      history: [],

      currentPlayingId: null,

      destroyedFlag: false
    };
  },


  created() {
    this.loadHistory();
  },


  beforeDestroy() {

    this.destroyedFlag = true;

    const player = this.$refs.globalPlayer;

    if (player) {

      player.pause();

      player.removeAttribute('src');

      try {
        player.load();
      } catch (e) {
        // ignore
      }
    }
  },


  methods: {

    /**
     * =========================================
     * DeepSeek
     * =========================================
     */

    async callDeepSeek(prompt, maxTokens) {

      const response = await axios.post(
        DEEPSEEK_API,
        {
          model: DEEPSEEK_MODEL,

          messages: [
            {
              role: 'system',
              content:
                '你是一个专业的中文 AI 音乐创作助手。' +
                '严格遵守用户给出的场景、曲风、主题和已有想法，' +
                '只输出最终需要的内容，不解释。'
            },
            {
              role: 'user',
              content: prompt
            }
          ],

          /**
           * 简单生成歌词不需要推理模式，
           * 可以降低延迟。
           */
          thinking: {
            type: 'disabled'
          },

          stream: false,

          max_tokens: maxTokens || 1200
        },
        {
          timeout: 60000
        }
      );

      const data = response.data || {};

      const choices = data.choices || [];

      if (!choices.length) {
        throw new Error('DeepSeek 未返回有效内容');
      }

      const message =
        choices[0] &&
        choices[0].message;

      const content =
        message &&
        message.content;

      if (!content) {
        throw new Error('DeepSeek 返回内容为空');
      }

      return String(content).trim();
    },


    /**
     * AI 生成歌曲想法
     */
    async generateIdea() {

      if (!this.form.scene) {
        return this.$message.warning(
          '请先选择场景'
        );
      }

      if (!this.form.style) {
        return this.$message.warning(
          '请先选择曲风'
        );
      }

      this.ideaLoading = true;

      try {

        const oldIdea =
          (this.form.idea || '').trim();

        const prompt = `
请根据下面的信息生成一段适合 AI 生歌使用的歌曲创作描述。

场景：${this.form.scene}
曲风：${this.form.style}

用户当前想法：
${oldIdea || '暂无'}

要求：

1. 如果用户已经输入想法，必须保留用户原本的核心意思，只进行完善。
2. 如果用户没有输入想法，则根据场景和曲风自动创作。
3. 包含歌曲主题、情绪、画面感、演唱感觉。
4. 50~100 字。
5. 不要写歌词。
6. 不要解释。
7. 只输出最终歌曲想法。
        `.trim();

        const content =
          await this.callDeepSeek(
            prompt,
            300
          );

        this.form.idea = content;

        this.$message.success(
          '歌曲想法生成成功'
        );

      } catch (error) {

        console.error(
          'generateIdea error:',
          error
        );

        this.$message.error(
          '想法生成失败：' +
          this.getErrorMessage(error)
        );

      } finally {

        this.ideaLoading = false;
      }
    },


    /**
     * AI 生成歌词
     */
    async generateLyrics() {

      if (!this.form.scene) {
        return this.$message.warning(
          '请先选择场景'
        );
      }

      if (!this.form.style) {
        return this.$message.warning(
          '请先选择曲风'
        );
      }

      this.lyricLoading = true;

      try {

        const prompt = `
请创作一首适合 AI 生歌模型演唱的中文歌曲歌词。

场景：
${this.form.scene}

曲风：
${this.form.style}

用户歌曲想法：
${this.form.idea || '没有额外想法，请根据场景和曲风创作'}

要求：

1. 用户歌曲想法优先级最高。
2. 不得改变用户明确表达的人物、主题、场景或情绪。
3. 歌词结构完整。
4. 使用以下结构标签：

[Intro]
[Verse]
[Pre-Chorus]
[Chorus]
[Verse 2]
[Bridge]
[Final Chorus]
[Outro]

5. Chorus 要有明确记忆点。
6. 语言自然，适合真实演唱。
7. 不要写创作说明。
8. 不要输出标题。
9. 只输出歌词。
        `.trim();

        const content =
          await this.callDeepSeek(
            prompt,
            1800
          );

        this.form.lyrics = content;

        this.$message.success(
          '歌词生成成功'
        );

      } catch (error) {

        console.error(
          'generateLyrics error:',
          error
        );

        this.$message.error(
          '歌词生成失败：' +
          this.getErrorMessage(error)
        );

      } finally {

        this.lyricLoading = false;
      }
    },


    /**
     * =========================================
     * AIMusic 生歌
     * =========================================
     */

    async generateSong() {

      if (this.songLoading) {
        return;
      }

      if (!this.form.scene) {
        return this.$message.warning(
          '请选择场景'
        );
      }

      if (!this.form.style) {
        return this.$message.warning(
          '请选择曲风'
        );
      }

      const lyrics =
        (this.form.lyrics || '').trim();

      if (!lyrics) {
        return this.$message.warning(
          '歌词不能为空'
        );
      }

      if (lyrics.length > 5000) {
        return this.$message.warning(
          '歌词不能超过 5000 个字符'
        );
      }

      this.songLoading = true;

      this.songStatusText =
        '正在提交歌曲生成任务...';

      try {

        /**
         * AIMusic Custom Mode
         *
         * prompt = 歌词
         * tags = 风格
         */
        const payload = {

          task_type:
            'create_music',

          custom_mode:
            true,

          mv:
            AIMUSIC_MODEL,

          prompt:
            lyrics,

          title:
            this.buildSongTitle(),

          tags:
            this.buildMusicTags(),

          /**
           * 风格遵循度
           */
          style_weight:
            0.8,

          /**
           * 创意程度
           *
           * 保守一些，
           * 避免歌曲与输入偏差太大。
           */
          weirdness_constraint:
            0.25
        };


        console.log(
          'AIMusic create request:',
          payload
        );


        const createResponse =
          await axios.post(
            AIMUSIC_CREATE_API,
            payload,
            {
              timeout: 30000
            }
          );


        console.log(
          'AIMusic create response:',
          createResponse.data
        );


        const createData =
          createResponse.data || {};


        if (
          typeof createData.code !==
            'undefined' &&
          Number(createData.code) !== 200
        ) {

          throw new Error(
            createData.message ||
            'AIMusic 创建任务失败'
          );
        }


        const taskId =
          createData.task_id;


        if (!taskId) {

          throw new Error(
            'AIMusic 未返回 task_id'
          );
        }


        this.songStatusText =
          '任务已提交，AI 正在作曲...';


        /**
         * 开始轮询任务
         */
        const songs =
          await this.pollAimusicTask(
            taskId
          );


        if (
          !songs ||
          !songs.length
        ) {

          throw new Error(
            '歌曲生成完成，但未找到有效音频'
          );
        }


        /**
         * AIMusic 通常一次会返回多首歌曲。
         */
        const newHistoryItems =
          songs.map(
            (song, index) => {

              return {

                id:
                  song.clip_id ||
                  (
                    taskId +
                    '-' +
                    index +
                    '-' +
                    Date.now()
                  ),

                taskId:
                  taskId,

                clipId:
                  song.clip_id || '',

                title:
                  song.title ||
                  this.buildSongTitle(),

                scene:
                  this.form.scene,

                style:
                  this.form.style,

                idea:
                  this.form.idea,

                lyrics:
                  song.lyrics ||
                  lyrics,

                tags:
                  song.tags ||
                  this.buildMusicTags(),

                url:
                  song.audio_url || '',

                imageUrl:
                  song.image_url || '',

                videoUrl:
                  song.video_url || '',

                duration:
                  Number(
                    song.duration || 0
                  ),

                model:
                  song.mv ||
                  AIMUSIC_MODEL,

                createdAt:
                  song.created_at ||
                  new Date().toISOString(),

                isPlaying:
                  false
              };
            }
          );


        /**
         * 新歌曲放在最前面。
         */
        this.history.unshift(
          ...newHistoryItems
        );


        this.saveHistory();


        this.$message.success(
          '歌曲生成完成，共 ' +
          newHistoryItems.length +
          ' 首'
        );


        this.songStatusText = '';

      } catch (error) {

        console.error(
          'generateSong error:',
          error
        );

        this.$message.error(
          '歌曲生成失败：' +
          this.getErrorMessage(error)
        );

        this.songStatusText = '';

      } finally {

        this.songLoading = false;
      }
    },


    /**
     * 轮询 AIMusic
     */
    async pollAimusicTask(taskId) {

      for (
        let count = 1;
        count <=
          AIMUSIC_MAX_POLL_COUNT;
        count++
      ) {

        if (this.destroyedFlag) {
          throw new Error(
            '页面已经关闭'
          );
        }


        /**
         * 官方建议不要频繁请求，
         * 15 秒后再查询。
         */
        await this.sleep(
          AIMUSIC_POLL_INTERVAL
        );


        if (this.destroyedFlag) {
          throw new Error(
            '页面已经关闭'
          );
        }


        this.songStatusText =
          'AI 正在作曲，正在等待结果...' +
          '（' +
          count +
          '/' +
          AIMUSIC_MAX_POLL_COUNT +
          '）';


        let response;

        try {

          response =
            await axios.get(
              AIMUSIC_TASK_API +
              encodeURIComponent(
                taskId
              ),
              {
                timeout: 30000
              }
            );

        } catch (error) {

          /**
           * 单次查询网络错误，
           * 不直接终止整个生歌任务。
           */
          console.warn(
            'AIMusic poll network error:',
            error
          );

          continue;
        }


        const body =
          response.data || {};


        console.log(
          'AIMusic task response:',
          body
        );


        if (
          typeof body.code !==
            'undefined' &&
          Number(body.code) !== 200
        ) {

          throw new Error(
            body.message ||
            '查询 AIMusic 任务失败'
          );
        }


        const rawData =
          body.data;


        /**
         * create_music 正常返回数组。
         */
        const songs =
          Array.isArray(rawData)
            ? rawData
            : [];


        /**
         * 部分任务刚创建时，
         * data 可能为空。
         */
        if (!songs.length) {

          const message =
            String(
              body.message || ''
            ).toLowerCase();


          if (
            message.indexOf(
              'fail'
            ) >= 0
          ) {

            throw new Error(
              body.message ||
              '歌曲生成失败'
            );
          }


          continue;
        }


        const successSongs =
          songs.filter(
            item => {

              if (!item) {
                return false;
              }

              /**
               * audio_url 已经存在，
               * 并且任务成功。
               */
              if (
                item.state ===
                  'succeeded' &&
                item.audio_url
              ) {
                return true;
              }

              /**
               * 兼容部分接口没有 state
               * 但已经返回 audio_url。
               */
              if (
                !item.state &&
                item.audio_url
              ) {
                return true;
              }

              return false;
            }
          );


        const runningSongs =
          songs.filter(
            item => {

              if (!item) {
                return false;
              }

              return (
                item.state ===
                  'pending' ||
                item.state ===
                  'running'
              );
            }
          );


        const failedSongs =
          songs.filter(
            item =>
              item &&
              item.state ===
                'failed'
          );


        /**
         * 所有任务都已经结束。
         */
        if (
          runningSongs.length === 0
        ) {

          if (
            successSongs.length > 0
          ) {

            return successSongs;
          }


          if (
            failedSongs.length > 0
          ) {

            throw new Error(
              'AIMusic 歌曲生成失败'
            );
          }
        }


        /**
         * 有些情况下第一首已经成功，
         * 第二首仍然 running。
         *
         * 继续等待，避免漏掉第二首歌。
         */
      }


      throw new Error(
        '歌曲生成等待超时，请稍后重新查询任务结果'
      );
    },


    /**
     * =========================================
     * AIMusic 参数
     * =========================================
     */

    buildSongTitle() {

      const scene =
        this.form.scene ||
        'AI';

      const style =
        this.form.style ||
        '音乐';

      return (
        scene +
        ' · ' +
        style
      );
    },


    buildMusicTags() {

      const styleMap = {

        '流行':
          'pop, modern, catchy melody, polished production',

        '古风':
          'Chinese traditional, guofeng, guzheng, erhu, cinematic, emotional',

        '摇滚':
          'rock, electric guitar, drums, energetic, powerful',

        '电子':
          'electronic, synthesizer, modern, rhythmic, atmospheric',

        '民谣':
          'folk, acoustic guitar, warm, intimate, storytelling',

        '说唱':
          'hip-hop, rap, rhythmic, modern beat',

        '轻音乐':
          'soft, relaxing, ambient, gentle, melodic'
      };


      const sceneMap = {

        '日常放松':
          'relaxing, peaceful, warm',

        '工作专注':
          'focused, steady, clean',

        '浪漫约会':
          'romantic, warm, dreamy',

        '运动健身':
          'energetic, powerful, upbeat',

        '生日祝福':
          'happy, uplifting, celebratory, warm',

        '毕业纪念':
          'nostalgic, uplifting, emotional',

        '旅行':
          'free, uplifting, cinematic, bright',

        '告白':
          'romantic, emotional, sincere, warm'
      };


      const style =
        styleMap[
          this.form.style
        ] ||
        this.form.style ||
        'pop';


      const scene =
        sceneMap[
          this.form.scene
        ] ||
        'emotional';


      return (
        style +
        ', ' +
        scene
      );
    },


    /**
     * =========================================
     * 播放
     * =========================================
     */

    async togglePlay(item) {

      if (!item || !item.url) {

        this.$message.warning(
          '当前歌曲没有有效音频地址'
        );

        return;
      }


      const player =
        this.$refs.globalPlayer;


      if (!player) {

        this.$message.error(
          '播放器初始化失败'
        );

        return;
      }


      /**
       * 点击当前歌曲
       */
      if (
        this.currentPlayingId ===
        item.id
      ) {

        if (!player.paused) {

          player.pause();

          item.isPlaying = false;

          return;
        }


        try {

          await player.play();

          item.isPlaying = true;

        } catch (error) {

          item.isPlaying = false;

          this.$message.error(
            '音频播放失败：' +
            this.getErrorMessage(
              error
            )
          );
        }

        return;
      }


      /**
       * 清理其他歌曲状态
       */
      this.history.forEach(
        historyItem => {

          historyItem.isPlaying =
            false;
        }
      );


      try {

        player.pause();

        player.src =
          item.url;

        player.load();

        await player.play();

        item.isPlaying = true;

        this.currentPlayingId =
          item.id;

      } catch (error) {

        item.isPlaying = false;

        this.currentPlayingId =
          null;

        this.$message.error(
          '音频播放失败：' +
          this.getErrorMessage(error)
        );
      }
    },


    onAudioEnded() {

      const item =
        this.history.find(
          historyItem =>
            historyItem.id ===
            this.currentPlayingId
        );


      if (item) {
        item.isPlaying = false;
      }


      this.currentPlayingId =
        null;
    },


    onAudioError() {

      const item =
        this.history.find(
          historyItem =>
            historyItem.id ===
            this.currentPlayingId
        );


      if (item) {
        item.isPlaying = false;
      }


      this.currentPlayingId =
        null;
    },


    /**
     * =========================================
     * 下载
     * =========================================
     */

    async downloadSong(item) {

      if (!item || !item.url) {

        return this.$message.warning(
          '暂无可下载音频'
        );
      }


      const fileName =
        this.buildDownloadFileName(
          item
        );


      try {

        /**
         * 优先通过 Blob 下载。
         *
         * 如果 AIMusic CDN 支持 CORS，
         * 浏览器会正常下载文件。
         */
        const response =
          await axios.get(
            item.url,
            {
              responseType:
                'blob',

              timeout:
                60000
            }
          );


        const blobUrl =
          URL.createObjectURL(
            response.data
          );


        const link =
          document.createElement(
            'a'
          );


        link.href =
          blobUrl;

        link.download =
          fileName;

        document.body.appendChild(
          link
        );

        link.click();

        document.body.removeChild(
          link
        );


        URL.revokeObjectURL(
          blobUrl
        );


      } catch (error) {

        /**
         * 如果 CDN 不允许跨域 fetch，
         * 退化为浏览器直接打开音频。
         */
        console.warn(
          'Blob download failed:',
          error
        );


        const link =
          document.createElement(
            'a'
          );


        link.href =
          item.url;

        link.target =
          '_blank';

        link.rel =
          'noopener noreferrer';


        document.body.appendChild(
          link
        );

        link.click();

        document.body.removeChild(
          link
        );


        this.$message.warning(
          '浏览器无法直接保存时，请在打开的音频页面中下载'
        );
      }
    },


    buildDownloadFileName(
      item
    ) {

      const title =
        (
          item.title ||
          'AI歌曲'
        )
          .replace(
            /[\\/:*?"<>|]/g,
            '_'
          );


      const extension =
        this.getAudioExtension(
          item.url
        );


      return (
        title +
        '.' +
        extension
      );
    },


    getAudioExtension(url) {

      const text =
        String(
          url || ''
        )
          .toLowerCase()
          .split('?')[0];


      if (
        text.endsWith('.wav')
      ) {
        return 'wav';
      }


      if (
        text.endsWith('.m4a')
      ) {
        return 'm4a';
      }


      if (
        text.endsWith('.aac')
      ) {
        return 'aac';
      }


      return 'mp3';
    },


    /**
     * =========================================
     * 历史
     * =========================================
     */

    saveHistory() {

      try {

        const saved =
          this.history
            .slice(0, 50)
            .map(
              item => ({
                ...item,
                isPlaying: false
              })
            );


        localStorage.setItem(
          HISTORY_STORAGE_KEY,
          JSON.stringify(saved)
        );

      } catch (error) {

        console.warn(
          '保存历史记录失败:',
          error
        );
      }
    },


    loadHistory() {

      try {

        const value =
          localStorage.getItem(
            HISTORY_STORAGE_KEY
          );


        if (!value) {
          return;
        }


        const data =
          JSON.parse(value);


        if (!Array.isArray(data)) {
          return;
        }


        this.history =
          data.map(
            item => ({
              ...item,
              isPlaying: false
            })
          );


      } catch (error) {

        console.warn(
          '读取历史记录失败:',
          error
        );
      }
    },


    clearHistory() {

      const player =
        this.$refs.globalPlayer;


      if (player) {

        player.pause();

        player.removeAttribute(
          'src'
        );
      }


      this.currentPlayingId =
        null;


      this.history = [];


      localStorage.removeItem(
        HISTORY_STORAGE_KEY
      );


      this.$message.success(
        '历史记录已清空'
      );
    },


    /**
     * =========================================
     * UI 工具
     * =========================================
     */

    getLyricsPreview(item) {

      const lyrics =
        item &&
        item.lyrics
          ? String(item.lyrics)
          : '暂无歌词';


      const clean =
        lyrics
          .replace(
            /\[[^\]]+\]/g,
            ''
          )
          .replace(
            /\s+/g,
            ' '
          )
          .trim();


      if (
        clean.length <= 45
      ) {
        return clean;
      }


      return (
        clean.substring(
          0,
          45
        ) +
        '...'
      );
    },


    formatDuration(seconds) {

      const total =
        Math.max(
          0,
          Math.floor(
            Number(
              seconds || 0
            )
          )
        );


      const minute =
        Math.floor(
          total / 60
        );


      const second =
        total % 60;


      return (
        minute +
        ':' +
        String(second)
          .padStart(
            2,
            '0'
          )
      );
    },


    sleep(ms) {

      return new Promise(
        resolve => {
          setTimeout(
            resolve,
            ms
          );
        }
      );
    },


    /**
     * 统一处理接口错误
     */
    getErrorMessage(error) {

      if (!error) {
        return '未知错误';
      }


      const response =
        error.response;


      if (
        response &&
        response.data
      ) {

        const data =
          response.data;


        if (
          typeof data ===
          'string'
        ) {

          return data;
        }


        if (
          data.error &&
          data.error.message
        ) {

          return data.error.message;
        }


        if (data.message) {

          return data.message;
        }


        if (data.msg) {

          return data.msg;
        }
      }


      if (error.message) {
        return error.message;
      }


      return '未知错误';
    }
  }
};
</script>


<style scoped>

.music-generator-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}


.title {
  text-align: center;
  margin-bottom: 30px;
  color: #333;
}


.input-with-button {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}


.flex-input {
  flex: 1;
}


.side-button {
  white-space: nowrap;
}


.generation-status {
  text-align: center;
  margin-top: 12px;
  font-size: 13px;
  color: #909399;
}


.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}


.empty-history {
  text-align: center;
  color: #999;
  padding: 40px 0;
}


.history-card {
  padding: 16px 0;
  border-bottom: 1px solid #eee;
}


.history-card:last-child {
  border-bottom: none;
}


.history-main {
  display: flex;
  align-items: center;
  gap: 12px;
}


.song-cover {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
}


.history-info {
  flex: 1;
  min-width: 0;
}


.song-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}


.song-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}


.tag {
  display: inline-block;
  font-size: 12px;
  color: #fff;
  background: #409eff;
  padding: 2px 8px;
  border-radius: 4px;
}


.duration {
  font-size: 12px;
  color: #909399;
}


.lyrics-preview {
  margin: 8px 0 0;
  font-size: 13px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}


.history-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 12px;
}


@media screen and (max-width: 768px) {

  .music-generator-container {
    padding: 12px;
  }


  .input-with-button {
    flex-direction: column;
  }


  .side-button {
    width: 100%;
  }


  .history-actions {
    justify-content: flex-start;
  }
}

</style>